/**
 * Launch tab of the editor (Paper "Experiments · 03 Launch · Settings panel"
 * and "03b Goal from site", "03c Traffic & audience", "03d Schedule, stop",
 * "03e Guardrails"). Settings are filled in from the page and the site, and
 * every change is written straight to the local draft.
 */
import { useMemo, useState, type ReactNode } from "react"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { estimateTestDuration } from "@/lib/utils/abTestStatistics"
import { formatNumber } from "@/lib/utils/abTestCalculations"
import { destinationCountsLogins } from "@/lib/experiments/outcomeLadder"
import {
  RESTRICTION_CHANNELS,
  RESTRICTION_DEVICES,
  type RestrictionChannel,
  type RestrictionDevice,
} from "@/lib/types/experiment-restrictions"
import { DEFAULT_PLAN, formatAbout } from "@/components/experiments/newTestModel"
import { EXPERIMENT_GOALS, findExperimentGoal } from "@/components/experiments/experimentGoals"
import type { DraftSplitTestState } from "@/components/experiments/splitTestDraft"

type View = "main" | "goal" | "traffic" | "audience" | "schedule" | "guardrails"

/** What the goal counts, for "Enough to spot a 20% change in …". */
const GOAL_NOUN: Record<string, string> = {
  form_submissions: "form submissions",
  click_through_rate: "clicks",
  page_funnel: "visits to the destination page",
  external_page_funnel: "outbound clicks",
  revenue: "payments",
  session_time: "time on page",
  bounce_rate: "bounce rate",
}

/**
 * The estimate assumes one conversion rate for every goal; say which way the
 * real goal moves it, so "about 3 weeks" for payments is not read as a promise.
 */
const GOAL_PACE: Record<string, string> = {
  click_through_rate: "Clicks are more common, so this is usually faster.",
  revenue: "Payments are usually much rarer, so expect it to take far longer.",
  session_time: "Time on page is measured on every visitor, so this is usually faster.",
  bounce_rate: "Bounces are common, so this is usually faster.",
}

const SPLITS: Array<{ value: [number, number]; label: string; hint: string }> = [
  { value: [50, 50], label: "50 / 50", hint: "Fastest to a result" },
  { value: [70, 30], label: "70 / 30", hint: "Most visitors keep the current page" },
  { value: [90, 10], label: "90 / 10", hint: "Careful rollout, much slower to call" },
]

const STOP_AFTER: Array<{ value: number | null; label: string }> = [
  { value: null, label: "When I stop it" },
  { value: 14, label: "After 2 weeks" },
  { value: 28, label: "After 4 weeks" },
  { value: 42, label: "After 6 weeks" },
]

const DEVICE_LABEL: Record<RestrictionDevice, string> = { desktop: "Desktop", mobile: "Mobile", tablet: "Tablet" }
const CHANNEL_LABEL: Record<RestrictionChannel, string> = {
  organic: "Organic search",
  paid: "Paid ads",
  direct: "Direct",
  referral: "Referral",
}

type LaunchSettingsPanelProps = {
  draft: DraftSplitTestState
  onChange: (patch: Partial<DraftSplitTestState>) => void
  /** Unique visitors a day on the page under test, last 30 days */
  pageDaily: number | null
  changesCount: number
  /** Goal ids ranked for this site; the first is marked Recommended */
  suggestedGoalIds: string[]
  onBack: () => void
  onLaunch: () => void
  launching: boolean
  atLimit: boolean
}

export function LaunchSettingsPanel({
  draft,
  onChange,
  pageDaily,
  changesCount,
  suggestedGoalIds,
  onBack,
  onLaunch,
  launching,
  atLimit,
}: LaunchSettingsPanelProps) {
  const [view, setView] = useState<View>("main")
  const goal = findExperimentGoal(draft.testGoal)
  const recommended = suggestedGoalIds[0]

  const estimate = useMemo(() => {
    if (!pageDaily) return null
    const share = Math.min(...draft.trafficDistribution) / 100
    // An uneven split is as slow as its smaller arm: scale traffic to two even arms.
    const effectiveDaily = pageDaily * Math.max(0.02, share * 2)
    return estimateTestDuration(effectiveDaily, DEFAULT_PLAN.baseline / 100, DEFAULT_PLAN.mde, DEFAULT_PLAN.significance, 2)
  }, [pageDaily, draft.trafficDistribution])

  if (view === "goal") {
    return (
      <GoalView
        draft={draft}
        recommended={recommended}
        suggestedGoalIds={suggestedGoalIds}
        onDone={(patch) => {
          onChange(patch)
          setView("main")
        }}
        onCancel={() => setView("main")}
      />
    )
  }
  if (view !== "main") {
    return (
      <div className="flex min-h-0 grow flex-col">
        <SubviewHeader title={SUBVIEW_TITLE[view]} onBack={() => setView("main")} />
        <div className="flex min-h-0 grow flex-col overflow-y-auto px-5 pb-5">
          {view === "traffic" && <TrafficView draft={draft} onChange={onChange} />}
          {view === "audience" && <AudienceView draft={draft} onChange={onChange} />}
          {view === "schedule" && <ScheduleView draft={draft} onChange={onChange} />}
          {view === "guardrails" && <GuardrailsView />}
        </div>
        <div className="border-t border-border px-5 pb-[18px] pt-4">
          <button
            type="button"
            onClick={() => setView("main")}
            className="h-[34px] w-full rounded-lg text-[13px] font-medium text-ui-ink ring-1 ring-black/[0.08] transition-colors hover:bg-muted dark:ring-white/10"
          >
            Done
          </button>
        </div>
      </div>
    )
  }

  const goalMissingUrl = goal?.requiresFinishPage && !draft.finishPageUrl.trim()
  const blocked = changesCount === 0 || !goal || goalMissingUrl
  const blockedReason = changesCount === 0
    ? "Make at least one change on version B first."
    : !goal
      ? "Pick a goal first."
      : goalMissingUrl
        ? "Add the destination page for this goal."
        : null

  return (
    <div className="flex min-h-0 grow flex-col">
      <div className="min-h-0 grow overflow-y-auto">
        <div className="flex flex-col gap-1 px-5 pb-3 pt-5">
          <h2 className="text-base font-semibold leading-5 text-ui-title">Launch settings</h2>
          <p className="text-[13px] leading-[19px] text-foreground/60">
            Filled in from your page. Change anything before you launch.
          </p>
        </div>
        <div className="flex flex-col px-5">
          <button
            type="button"
            onClick={() => setView("goal")}
            className="mb-1 flex items-center gap-2.5 rounded-[10px] bg-primary-tint p-3 text-left ring-1 ring-primary-tint-ring transition-colors hover:bg-primary-tint/70"
          >
            <GoalMark />
            <span className="flex min-w-0 grow basis-0 flex-col gap-0.5">
              <span className="text-xs leading-4 text-foreground/60">Goal · decides the winner</span>
              <span className="text-sm font-semibold leading-[18px] text-ui-title">{goal?.name ?? "Pick a goal"}</span>
              <span className="text-xs leading-4 text-foreground/60">
                {goal?.requiresFinishPage
                  ? draft.finishPageUrl.trim() || "Destination page not set"
                  : goal?.description ?? "What the test should improve"}
              </span>
            </span>
            <span className="text-xs font-medium leading-4 text-foreground/60">Change</span>
          </button>

          <SettingRow icon={<TrafficIcon />} label="Traffic" value={`All visitors · ${draft.trafficDistribution.join(" / ")}`} onClick={() => setView("traffic")} />
          <SettingRow icon={<AudienceIcon />} label="Audience" value={audienceSummary(draft)} onClick={() => setView("audience")} />
          <SettingRow icon={<ScheduleIcon />} label="Schedule" value={scheduleSummary(draft.autoStopDurationDays)} onClick={() => setView("schedule")} />
          <SettingRow icon={<GuardrailIcon />} label="Guardrails" value="Revenue per visitor" onClick={() => setView("guardrails")} />

          <div className="flex flex-col gap-1.5 py-3.5">
            <p className="text-xs leading-4 text-foreground/60">Also recorded, for your next idea</p>
            <p className="text-xs leading-[19px] text-foreground/60">
              Clicks on every button and link, trial and demo clicks, time on page. Shown in results, never used to pick the winner.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t border-border px-5 pb-[18px] pt-4">
        <div className="flex flex-col gap-[7px]">
          {estimate ? (
            <>
              <div className="flex justify-between gap-3">
                <span className={cn("text-xs font-medium leading-4", estimate.days > 60 ? "text-warning" : "text-ui-ink")}>
                  {capitalize(formatAbout(estimate.days))}
                </span>
                <span className="text-right text-xs leading-4 text-foreground/60">
                  About {formatNumber(estimate.totalRequired)} visitors at {formatNumber(pageDaily ?? 0)}/day
                </span>
              </div>
              <span className="text-xs leading-4 text-foreground/60">
                Enough to spot a {DEFAULT_PLAN.mde}% change in {GOAL_NOUN[draft.testGoal] ?? "the goal"}, if about{" "}
                {DEFAULT_PLAN.baseline} in 100 visitors convert. {GOAL_PACE[draft.testGoal] ?? ""}
              </span>
            </>
          ) : (
            <span className="text-xs leading-4 text-foreground/60">
              Time to a result shows once this page has traffic in the last 30 days.
            </span>
          )}
          {blockedReason && <span className="text-xs leading-4 text-warning">{blockedReason}</span>}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onBack}
            className="h-[34px] w-24 shrink-0 rounded-lg text-[13px] font-medium text-ui-ink ring-1 ring-black/[0.08] transition-colors hover:bg-muted dark:ring-white/10"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onLaunch}
            disabled={Boolean(blocked) || launching}
            className="h-[34px] grow basis-0 rounded-lg bg-primary text-[13px] font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {launching ? "Launching…" : atLimit ? "Upgrade to launch" : "Launch test"}
          </button>
        </div>
      </div>
    </div>
  )
}

const SUBVIEW_TITLE: Record<Exclude<View, "main" | "goal">, string> = {
  traffic: "Who sees which version?",
  audience: "Who is in the test?",
  schedule: "When does it stop?",
  guardrails: "What must not get worse?",
}

function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function audienceSummary(draft: DraftSplitTestState): string {
  const countries = draft.geoRestriction?.countries?.filter(Boolean) ?? []
  const devices = draft.audienceRestriction?.devices?.filter(Boolean) ?? []
  const channels = draft.audienceRestriction?.channels?.filter(Boolean) ?? []
  const where = countries.length ? `${countries.length} ${countries.length === 1 ? "country" : "countries"}` : "All countries"
  const what = devices.length ? devices.map((d) => DEVICE_LABEL[d].toLowerCase()).join(", ") : "all devices"
  return channels.length ? `${where} · ${what} · ${channels.length} sources` : `${where} · ${what}`
}

function scheduleSummary(days: number | null): string {
  if (!days) return "Start now · stop by hand"
  return `Start now · stop after ${days % 7 === 0 ? `${days / 7} weeks` : `${days} days`}`
}

function SubviewHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="flex flex-col gap-1 px-5 pb-3 pt-5">
      <button type="button" onClick={onBack} className="flex w-fit items-center gap-1 text-xs leading-4 text-foreground/60 hover:text-foreground">
        <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden><path d="m15 18-6-6 6-6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        Launch settings
      </button>
      <h2 className="text-base font-semibold leading-5 text-ui-title">{title}</h2>
    </div>
  )
}

function SettingRow({ icon, label, value, onClick }: { icon: ReactNode; label: string; value: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-[52px] shrink-0 items-center gap-2.5 border-b border-ui-rowline text-left transition-colors hover:bg-muted/40"
    >
      <span className="flex size-4 shrink-0 items-center justify-center text-ui-icon">{icon}</span>
      <span className="w-20 shrink-0 text-[13px] leading-4 text-foreground/60">{label}</span>
      <span className="min-w-0 grow basis-0 truncate text-[13px] font-medium leading-4 text-ui-ink">{value}</span>
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden className="shrink-0 text-ui-meta"><path d="m9 18 6-6-6-6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
    </button>
  )
}

function OptionRow({
  selected,
  onClick,
  title,
  hint,
  badge,
  type = "radio",
}: {
  selected: boolean
  onClick: () => void
  title: string
  hint?: string
  badge?: string
  type?: "radio" | "check"
}) {
  return (
    <button
      type="button"
      role={type === "radio" ? "radio" : "checkbox"}
      aria-checked={selected}
      onClick={onClick}
      className={cn(
        "flex items-start gap-2.5 rounded-[10px] px-3 py-2.5 text-left transition-colors",
        selected ? "bg-primary-tint ring-1 ring-primary-tint-ring" : "hover:bg-muted/50",
      )}
    >
      <span
        className={cn(
          "mt-px flex size-4 shrink-0 items-center justify-center border",
          type === "radio" ? "rounded-full" : "rounded-[4px]",
          selected ? "border-ui-title bg-ui-title" : "border-input bg-background",
        )}
        aria-hidden
      >
        {selected && (type === "radio"
          ? <span className="size-1.5 rounded-full bg-background" />
          : <svg width="10" height="10" viewBox="0 0 24 24"><path d="m5 12 5 5 9-10" fill="none" stroke="currentColor" strokeWidth="3" className="text-background" strokeLinecap="round" strokeLinejoin="round" /></svg>)}
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="flex items-center gap-1.5 text-sm font-medium leading-[18px] text-ui-title">
          {title}
          {badge && (
            <span className="rounded-[5px] bg-primary-tint px-1.5 text-xs font-normal leading-[18px] text-primary-strong ring-1 ring-primary-tint-ring">
              {badge}
            </span>
          )}
        </span>
        {hint && <span className="text-xs leading-4 text-foreground/60">{hint}</span>}
      </span>
    </button>
  )
}

function GoalView({
  draft,
  recommended,
  suggestedGoalIds,
  onDone,
  onCancel,
}: {
  draft: DraftSplitTestState
  recommended: string | undefined
  suggestedGoalIds: string[]
  onDone: (patch: Partial<DraftSplitTestState>) => void
  onCancel: () => void
}) {
  const [pick, setPick] = useState(draft.testGoal || recommended || "")
  const [finishUrl, setFinishUrl] = useState(draft.finishPageUrl)
  const ordered = useMemo(() => {
    const rank = (id: string) => {
      const i = suggestedGoalIds.indexOf(id)
      return i === -1 ? suggestedGoalIds.length + EXPERIMENT_GOALS.findIndex((g) => g.id === id) : i
    }
    return [...EXPERIMENT_GOALS].sort((a, b) => rank(a.id) - rank(b.id))
  }, [suggestedGoalIds])
  const picked = findExperimentGoal(pick)
  const needsUrl = Boolean(picked?.requiresFinishPage)
  const canUse = Boolean(picked) && (!needsUrl || finishUrl.trim().length > 0)

  return (
    <div className="flex min-h-0 grow flex-col">
      <SubviewHeader title="What should this test improve?" onBack={onCancel} />
      <div className="flex min-h-0 grow flex-col gap-1 overflow-y-auto px-4 pb-4" role="radiogroup" aria-label="Goal">
        {ordered.map((g) => (
          <div key={g.id} className="flex flex-col">
            <OptionRow
              selected={pick === g.id}
              onClick={() => setPick(g.id)}
              title={g.name}
              hint={g.description}
              badge={g.id === recommended ? "Recommended" : undefined}
            />
            {pick === g.id && g.requiresFinishPage && (
              <div className="flex flex-col gap-1.5 px-3 pb-2 pt-2">
                <label className="text-xs leading-4 text-foreground/60" htmlFor="launch-finish-url">
                  {g.id === "external_page_funnel" ? "External page that counts as a conversion" : "Page that counts as a conversion"}
                </label>
                <Input
                  id="launch-finish-url"
                  value={finishUrl}
                  onChange={(e) => setFinishUrl(e.target.value)}
                  placeholder={g.id === "external_page_funnel" ? "https://partner.com/thanks" : "/thank-you"}
                  className="h-8 text-[13px]"
                />
                {destinationCountsLogins(finishUrl) && (
                  <p className="text-xs leading-4 text-warning">
                    Returning customers log in there too, so logins would count as conversions. Pick a page only new sign-ups reach.
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-3 border-t border-border px-5 pb-[18px] pt-4">
        <p className="text-xs leading-[18px] text-foreground/60">
          The goal is locked at launch. Every other metric still shows in results.
        </p>
        <button
          type="button"
          disabled={!canUse}
          onClick={() => onDone({ testGoal: pick, finishPageUrl: needsUrl ? finishUrl.trim() : "" })}
          className="h-[34px] w-full rounded-lg bg-primary text-[13px] font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {picked ? `Use ${picked.name.toLowerCase()}` : "Pick a goal"}
        </button>
      </div>
    </div>
  )
}

function TrafficView({ draft, onChange }: { draft: DraftSplitTestState; onChange: (patch: Partial<DraftSplitTestState>) => void }) {
  const current = draft.trafficDistribution.join("/")
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1" role="radiogroup" aria-label="Traffic split">
        <p className="px-1 pb-1 text-xs leading-4 text-foreground/60">Split between Control and B</p>
        {SPLITS.map((s) => (
          <OptionRow
            key={s.label}
            selected={current === s.value.join("/")}
            onClick={() => onChange({ trafficDistribution: [...s.value] })}
            title={s.label}
            hint={s.hint}
          />
        ))}
      </div>
      <label className="flex items-start justify-between gap-3 rounded-[10px] px-3 py-2.5 ring-1 ring-ui-hairline">
        <span className="flex flex-col gap-0.5">
          <span className="text-sm font-medium leading-[18px] text-ui-title">Same version on every visit</span>
          <span className="text-xs leading-4 text-foreground/60">
            A returning visitor keeps the version they saw first. Off: each visit is assigned again.
          </span>
        </span>
        <Switch checked={draft.isUserLevel} onCheckedChange={(v) => onChange({ isUserLevel: v })} aria-label="Same version on every visit" />
      </label>
    </div>
  )
}

function AudienceView({ draft, onChange }: { draft: DraftSplitTestState; onChange: (patch: Partial<DraftSplitTestState>) => void }) {
  const devices = draft.audienceRestriction?.devices ?? []
  const channels = draft.audienceRestriction?.channels ?? []
  const countries = draft.geoRestriction?.countries?.filter(Boolean) ?? []
  const toggle = <T,>(list: T[], value: T) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value])
  const setAudience = (next: { devices?: RestrictionDevice[]; channels?: RestrictionChannel[] }) =>
    onChange({
      audienceRestriction: {
        enabled: (next.devices ?? devices).length + (next.channels ?? channels).length > 0,
        devices: next.devices ?? devices,
        channels: next.channels ?? channels,
      },
    })
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="px-1 pb-1 text-xs leading-4 text-foreground/60">Devices · none picked means all</p>
        {RESTRICTION_DEVICES.map((d) => (
          <OptionRow key={d} type="check" selected={devices.includes(d)} onClick={() => setAudience({ devices: toggle(devices, d) })} title={DEVICE_LABEL[d]} />
        ))}
      </div>
      <div className="flex flex-col gap-1">
        <p className="px-1 pb-1 text-xs leading-4 text-foreground/60">Traffic sources · none picked means all</p>
        {RESTRICTION_CHANNELS.map((c) => (
          <OptionRow key={c} type="check" selected={channels.includes(c)} onClick={() => setAudience({ channels: toggle(channels, c) })} title={CHANNEL_LABEL[c]} />
        ))}
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="px-1 text-xs leading-4 text-foreground/60" htmlFor="launch-countries">
          Countries · two-letter codes, comma separated, empty means all
        </label>
        <Input
          id="launch-countries"
          defaultValue={countries.join(", ")}
          onBlur={(e) => {
            const list = e.target.value.split(/[\s,]+/).map((c) => c.trim().toUpperCase()).filter((c) => /^[A-Z]{2}$/.test(c))
            onChange({ geoRestriction: { enabled: list.length > 0, countries: list } })
          }}
          placeholder="US, CA, GB"
          className="h-8 text-[13px]"
        />
      </div>
    </div>
  )
}

function ScheduleView({ draft, onChange }: { draft: DraftSplitTestState; onChange: (patch: Partial<DraftSplitTestState>) => void }) {
  return (
    <div className="flex flex-col gap-1" role="radiogroup" aria-label="Stop">
      <p className="px-1 pb-1 text-xs leading-4 text-foreground/60">The test starts as soon as you launch it.</p>
      {STOP_AFTER.map((s) => (
        <OptionRow
          key={s.label}
          selected={(draft.autoStopDurationDays ?? null) === s.value}
          onClick={() => onChange({ autoStopDurationDays: s.value })}
          title={s.label}
          hint={s.value == null ? "Results tell you when it is safe to call" : "Stops on that day, called or not"}
        />
      ))}
    </div>
  )
}

function GuardrailsView() {
  return (
    <div className="flex flex-col gap-3 text-[13px] leading-[19px] text-foreground/70">
      <p>
        Revenue per visitor is watched on every test once Stripe is connected. If it drops on version B, results say
        <span className="font-medium text-ui-title"> Check first</span> before you apply it.
      </p>
      <p>Stripe steps settle 14 days after the last visitor, so a revenue drop is only called after that.</p>
    </div>
  )
}

function GoalMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden className="shrink-0 text-primary">
      <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="3" fill="currentColor" />
    </svg>
  )
}
function TrafficIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden><path d="M4 7h11M4 17h7M15 4l3 3-3 3M11 14l3 3-3 3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
}
function AudienceIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <circle cx="9" cy="8" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3.5 19c.8-3 3-4.5 5.5-4.5s4.7 1.5 5.5 4.5M16 5.5a3 3 0 0 1 0 5.5M18 14.8c1.3.7 2.1 2 2.5 4.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}
function ScheduleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 8v4l2.5 2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}
function GuardrailIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden><path d="M12 3.5 5 6.5v5c0 4.2 3 7.5 7 9 4-1.5 7-4.8 7-9v-5z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>
}

export default LaunchSettingsPanel
